import { useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { Guid } from '@user/utils/Guid'
import { fileMimeTypes } from '../typings'
import { addAttachments, ICreateAttachmentInput } from '@user/store/attachments'
import { onDropRejected } from '../functions'
import { ENTITY_TYPES, useAppDispatch } from '@sales-center/common'
import { toast } from '@sales-center/components'
import { MAX_FILE_SIZE } from '../../constants'
import { validateFileName } from '../../validateFileName'

type Params = {
  entityId: number
  entityType: ENTITY_TYPES
  canUpdate: boolean
}

export function useAttachmentUpload(params: Params) {
  const { entityId, entityType, canUpdate } = params

  const dispatch = useAppDispatch()

  const handleFilesAdded = useCallback(
    (files: FileList) => {
      let count = 0
      let successCount = 0
      const addFile = async (file: File) => {
        const createAttachmentInput: ICreateAttachmentInput = {
          displayName: file.name,
          fileSize: file.size,
          id: Guid.newGuid(),
          isLeadAttached: entityType === ENTITY_TYPES.Lead,
          mimeType: file.type,
          oppLeadId: entityId,
          streamContent: file,
        }
        const result = await dispatch(addAttachments(createAttachmentInput))
        if (addAttachments.fulfilled.match(result)) {
          successCount++
        }
        count++
        if (count == files.length && successCount > 0) {
          toast.success('File(s) successfully uploaded.')
        }
      }

      if (files && files.length) {
        for (let i = 0; i < files.length; i++) {
          addFile(files[i])
        }
      }
    },
    [dispatch, entityId, entityType],
  )

  const onDrop = useCallback(
    (acceptedFiles) => handleFilesAdded(acceptedFiles),
    [handleFilesAdded],
  )

  const { open: openFileSelect, getInputProps } = useDropzone({
    maxSize: MAX_FILE_SIZE,
    onDrop,
    onDropRejected,
    accept: fileMimeTypes,
    validator: validateFileName,
    noClick: true,
    disabled: !canUpdate,
  })

  return {
    openFileSelect,
    getInputProps,
    handleFilesAdded,
  }
}
